import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { readStoredLocation, storeCity } from '@/lib/catalog';
import { track } from '@/lib/analytics';

/**
 * Cross-page UI state only. Event data itself comes from `hooks/useCatalog`,
 * which talks to the backend — nothing here is hardcoded content.
 */
const EventContext = createContext(null);

export const useEvents = () => {
  const context = useContext(EventContext);
  if (!context) throw new Error('useEvents must be used within an EventProvider');
  return context;
};

export const EventProvider = ({ children }) => {
  const [selectedLocation, setSelectedLocation] = useState(
    () => readStoredLocation().name || 'Bikaner'
  );
  const [searchQuery, setSearchQuery] = useState('');

  /**
   * The city the visitor picked: { zoneId, name, lat, lng }. `zoneId` is null
   * for visitors who picked a city before cities came from the admin (only a
   * name was ever stored); useCity() matches those by name.
   */
  const [selectedCity, setSelectedCityState] = useState(() => {
    const stored = readStoredLocation();
    return { zoneId: stored.zoneId ?? null, name: stored.name || 'Bikaner', lat: stored.lat, lng: stored.lng };
  });

  const setSelectedCity = useCallback((city) => {
    if (!city) return;
    storeCity(city);
    setSelectedCityState(city);
    setSelectedLocation(city.name);
    track('city_selected', { zoneId: city.zoneId, name: city.name });
  }, []);

  /**
   * Record what people searched for - including searches that found nothing,
   * which are the most useful ones. Debounced so a query is logged once the
   * visitor stops typing, not once per keystroke.
   */
  useEffect(() => {
    const term = searchQuery.trim();
    if (term.length < 3) return undefined;
    const timer = setTimeout(() => track('search', { query: term }), 800);
    return () => clearTimeout(timer);
  }, [searchQuery]);
  const [bookingDetails, setBookingDetails] = useState(null);

  // Keep the header label in sync when the location modal writes to storage.
  useEffect(() => {
    const sync = () => {
      const stored = readStoredLocation();
      setSelectedLocation(stored.name || 'Bikaner');
      setSelectedCityState({ zoneId: stored.zoneId ?? null, name: stored.name || 'Bikaner', lat: stored.lat, lng: stored.lng });
    };
    window.addEventListener('storage', sync);
    return () => window.removeEventListener('storage', sync);
  }, []);

  const value = useMemo(
    () => ({
      selectedLocation,
      setSelectedLocation,
      selectedCity,
      setSelectedCity,
      searchQuery,
      setSearchQuery,
      bookingDetails,
      setBookingDetails,
    }),
    [selectedLocation, selectedCity, setSelectedCity, searchQuery, bookingDetails]
  );

  return <EventContext.Provider value={value}>{children}</EventContext.Provider>;
};
